import React from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import IconFemtasy from 'components/IconFemtasy';
import { FemtasyIcon } from 'components/IconFemtasy/iconsType';

import { IntlMsgShape, convertToString } from 'helpers/consts';

import { ContentType } from 'types/contentType';

export enum PillType {
  Outlined = 'Pill__chip',
  Filled = 'FilledPill__chip',
}

interface IProps {
  ariaLabel?: string;
  leftIcon?: FemtasyIcon;
  linkTo?: string;
  deleteIcon?: boolean;
  isSelected: boolean;
  strikeThroughFormat?: boolean;
  message: IntlMsgShape | string;
  onClick?: Function;
  pillType?: PillType;
}

export const contentTypeSlugToIconType = (contentTypeSlug: ContentType) => {
  switch (contentTypeSlug) {
    case ContentType.Series:
      return 'Seriesline';
    case ContentType.Soul:
      return 'Soul';
    case ContentType.Sounds:
      return 'Sounds';
    case ContentType.Story:
    default:
      return 'Story';
  }
};

const PillWrapper = ({
  children,
  linkTo,
}: {
  children: React.ReactNode;
  linkTo?: string;
}) => {
  if (linkTo) {
    return (
      <Link data-testid="PillTest" className="Pill" to={linkTo}>
        {children}
      </Link>
    );
  }

  return (
    <div data-testid="PillTest" className="Pill">
      {children}
    </div>
  );
};

const Pill = ({
  ariaLabel,
  deleteIcon = false,
  isSelected,
  leftIcon,
  linkTo,
  message,
  onClick,
  pillType = PillType.Outlined,
  strikeThroughFormat,
}: IProps) => {
  const { formatMessage } = useIntl();

  const clickable = Boolean(onClick) && !linkTo;

  const chipClasses = classNames(
    pillType,
    'inline-flex h-8 max-w-full items-center justify-center whitespace-nowrap rounded-2xl font-sans',
    pillType === PillType.Outlined && 'border border-solid border-light-body',
    pillType === PillType.Filled && 'p-1',
    isSelected
      ? 'Pill--selected bg-light-body text-dark-body'
      : pillType === PillType.Filled
      ? 'bg-soft-sensual-audios text-white'
      : 'bg-transparent text-white',
    clickable && 'cursor-pointer select-none',
  );

  return (
    <PillWrapper linkTo={linkTo}>
      <div
        aria-label={ariaLabel}
        className={chipClasses}
        onClick={() => onClick && onClick()}
        role={clickable ? 'button' : undefined}
      >
        {leftIcon && (
          <IconFemtasy
            className="-mr-1.5 ml-3"
            color="inherit"
            icon={leftIcon}
            size="14"
          />
        )}
        <span
          className={classNames(
            'Pill__label overflow-hidden text-ellipsis whitespace-nowrap px-3 text-xs font-normal leading-4',
            strikeThroughFormat && 'line-through',
          )}
        >
          {convertToString(message, formatMessage)}
        </span>
        {deleteIcon && (
          <IconFemtasy
            className="-ml-1.5 mr-1"
            color="inherit"
            icon="Cancel"
            onClick={(event) => {
              event.stopPropagation();
              onClick && onClick();
            }}
            size="14"
          />
        )}
      </div>
    </PillWrapper>
  );
};

export default Pill;
